import { parseSoleMoneyCents } from "./figures";
import type { BudgetSignal } from "./railPicks";

/**
 * X3 RAIL: the three pure readers `rail.prefill` feeds the pickers with. None
 * of them returns a customer's words: tags are catalog vocabulary, the budget
 * is a number in cents (section 10; Composer 3).
 */

function asTag(v: unknown): string | null {
  if (typeof v === "string") {
    const t = v.trim().toLowerCase();
    return t === "" ? null : t;
  }
  if (v !== null && typeof v === "object") {
    const o = v as Record<string, unknown>;
    return asTag(o["tag"]) ?? asTag(o["value"]);
  }
  return null;
}

/**
 * The descriptors a catalog record carries: the string values of its `specs`
 * object and the tags in its `vision_tags` list (a string, or an object with a
 * `tag` or `value`). Lowercased, de-duplicated, sorted. Numbers are not tags.
 */
export function railTagsOf(specs: unknown, visionTags: unknown): string[] {
  const out = new Set<string>();
  if (specs !== null && typeof specs === "object" && !Array.isArray(specs)) {
    for (const v of Object.values(specs as Record<string, unknown>)) {
      if (typeof v === "string") {
        const t = asTag(v);
        if (t !== null) out.add(t);
      }
    }
  }
  if (Array.isArray(visionTags)) {
    for (const v of visionTags) {
      const t = asTag(v);
      if (t !== null) out.add(t);
    }
  }
  return [...out].sort();
}

const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Which catalog words the customer used. `vocabulary` is the union of the
 * candidates' tags, so the result can only ever be a word the shop sells by;
 * `texts` are read in memory and dropped. Whole words, case-insensitive.
 */
export function matchConversationTags(vocabulary: Iterable<string>, texts: readonly string[]): string[] {
  const haystack = texts.join("\n").toLowerCase();
  const out: string[] = [];
  for (const tag of new Set([...vocabulary].map((t) => t.trim().toLowerCase()).filter((t) => t !== ""))) {
    if (new RegExp(`(?<![\\p{L}\\p{N}])${escapeRe(tag)}(?![\\p{L}\\p{N}])`, "u").test(haystack)) out.push(tag);
  }
  return out.sort();
}

/**
 * The ceiling from the contact's ACTIVE `dm.stated-budget` memory rows (held
 * ones are contradictions and never reach here). Each row must hold exactly one
 * dollar figure the shared money reader (F-16) reads whole; a range, a "5k" or a
 * per-month figure reads as nothing. With several readable rows the LOWEST wins.
 * No readable row is `null`, which the picker's why line says as "no budget
 * stated": a band is never invented. Memory is data, never instructions
 * (section 6): this only narrows what the rail suggests to the owner, and
 * states nothing to a customer.
 */
export function budgetSignalOf(statedBudgetRows: readonly string[]): BudgetSignal {
  const cents = statedBudgetRows.map((r) => parseSoleMoneyCents(r)).filter((c): c is number => c !== null && c > 0);
  return cents.length === 0 ? null : { maxCents: Math.min(...cents) };
}
